<template>
  <AppHeaderBack title="Update Profile Picture" />
  <div class="min-h-screen bg-blue-50">
    <!-- Header section with UP colors -->
    <div class="bg-gradient-to-r from-blue-900 to-blue-700 py-8 shadow-md">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col items-center">
          <div class="text-center">
            <h1 class="text-2xl font-bold text-white mb-1">Update Profile Picture</h1>
            <p class="text-blue-100">Upload a new profile photo</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div class="grid grid-cols-1 max-w-xl mx-auto">
        <div class="bg-white rounded-lg shadow transform transition-transform duration-300 hover:-translate-y-1 hover:shadow-md">
          <div class="p-6">
            <!-- Profile Picture Preview -->
            <div v-if="!isCropping" class="flex flex-col items-center mb-8">
              <div class="relative">
                <img 
                  :src="imagePreview || (isFirebaseStorageURL(profile?.profilePicture) ? profile?.profilePicture : defaultProfilePicUse)" 
                  alt="Profile Picture" 
                  class="w-32 h-32 rounded-full object-cover border-4 border-yellow-500 shadow-lg"
                />
                <div v-if="profile?.profilePicture" class="absolute -bottom-3 left-1/2 transform -translate-x-1/2">
                  <button 
                    @click="deleteProfilePic" 
                    :disabled="loading"
                    class="flex items-center justify-center p-2 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-full shadow-md hover:from-red-700 hover:to-pink-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <!-- Image Cropper View -->
            <div v-if="isCropping" class="mb-6">
              <div class="relative h-96 bg-blue-50 rounded-lg overflow-hidden flex items-center justify-center">
                <img 
                  ref="image" 
                  :src="imagePreview" 
                  alt="Preview" 
                  class="w-full h-full object-contain"
                />
              </div>
              <div class="mt-4 flex justify-center space-x-3">
                <button 
                  @click="cancelCrop"
                  class="px-4 py-2 border border-blue-900 rounded-lg text-blue-900 bg-white hover:bg-blue-50 transition-all duration-200 focus:outline-none"
                >
                  Cancel
                </button>
                <button 
                  @click="confirmCrop" 
                  :disabled="loading || !cropper"
                  class="px-4 py-2 bg-gradient-to-r from-blue-900 to-blue-700 text-white rounded-lg hover:from-blue-800 hover:to-blue-600 transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply Crop
                </button>
              </div>
            </div>

            <!-- File Upload Section -->
            <div class="space-y-6">
              <label class="block">
                <span class="sr-only">Choose profile photo</span>
                <input 
                  type="file" 
                  @change="handleFileUpload" 
                  accept="image/*" 
                  :disabled="loading"
                  class="block w-full text-sm text-blue-900 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-900 hover:file:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </label>

              <button 
                v-if="croppedBlob && !isCropping"
                @click="uploadProfilePic" 
                :disabled="!croppedBlob || loading"
                class="w-full flex justify-center items-center py-3 px-4 bg-gradient-to-r from-blue-900 to-blue-700 text-white rounded-lg shadow-md hover:from-blue-800 hover:to-blue-600 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg v-if="loading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ loading ? "Uploading..." : "Save Profile Picture" }}
              </button>

              <!-- Cancel Button -->
              <router-link 
                to="/profile"
                class="w-full flex justify-center items-center py-3 px-4 rounded-lg font-semibold text-blue-900 border border-blue-900 bg-white hover:bg-blue-50 transition"
              >
                Cancel
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppHeaderBack from '@/components/common/AppHeaderBack.vue';
import { mapState, mapMutations } from "vuex";
import { uploadProfilePicture, deleteProfilePicture } from "@/composables/useStorage";
import { getUserProfile } from "@/composables/useFirestore";
import { isFirebaseStorageURL } from "@/composables/useGlobalFunctions";
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';
import defaultProfilePic from '../../assets/profileIcon.png';

export default {
  components: {
    AppHeaderBack,
  },
  data() {
    return {
      selectedFile: null,
      imagePreview: null,
      cropper: null,
      croppedBlob: null,
      isCropping: false,
      defaultProfilePicUse: defaultProfilePic,
    };
  },
  computed: {
    ...mapState(["user", "profile", "loading"]),
  },
  methods: {
    ...mapMutations(["setLoading", "setProfile"]),
    isFirebaseStorageURL,

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file type
      if (!file.type.match('image.*')) {
        this.$store.dispatch("showAlert", { 
          message: "Please select an image file", 
          type: "error" 
        });
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.$store.dispatch("showAlert", { 
          message: "Image size should be less than 5MB", 
          type: "error" 
        });
        return;
      }

      this.selectedFile = file;
      this.imagePreview = URL.createObjectURL(this.selectedFile);
      this.isCropping = true;

      this.$nextTick(() => {
        if (this.cropper) {
          this.cropper.destroy();
        }

        const imageElement = this.$refs.image;
        if (!imageElement) return;

        this.cropper = new Cropper(imageElement, {
          aspectRatio: 1,
          viewMode: 1,
          autoCropArea: 1,
          scalable: false,
          rotatable: false,
          zoomable: true,
          responsive: true,
          background: false,
        });
      });
    },

    cancelCrop() {
      this.isCropping = false;
      this.imagePreview = null;
      this.croppedBlob = null;
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
    },

    confirmCrop() {
      if (!this.cropper) return;

      const canvas = this.cropper.getCroppedCanvas({
        width: 512,
        height: 512,
        minWidth: 256,
        minHeight: 256,
        maxWidth: 1024,
        maxHeight: 1024,
        fillColor: '#fff',
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });

      if (!canvas) return;

      canvas.toBlob((blob) => {
        this.croppedBlob = blob;
        this.imagePreview = canvas.toDataURL("image/jpeg", 0.9);
        this.isCropping = false;
      }, 'image/jpeg', 0.9);
    },

    async uploadProfilePic() {
      if (!this.croppedBlob) {
        this.$store.dispatch("showAlert", { 
          message: "Please select and crop an image", 
          type: "error" 
        });
        return;
      }

      try {
        this.setLoading(true);
        const result = await uploadProfilePicture(this.user.uid, this.croppedBlob);
        
        if (result.success) {
          const updatedProfile = await getUserProfile(this.user.uid);
          this.setProfile(updatedProfile);
          this.$store.dispatch("showAlert", { 
            message: "Profile picture updated successfully!", 
            type: "success" 
          });
          this.croppedBlob = null;
        } else {
          throw new Error(result.message);
        }
      } catch (error) {
        this.$store.dispatch("showAlert", { 
          message: error.message || "Failed to upload profile picture", 
          type: "error" 
        });
      } finally {
        this.setLoading(false);
      }
    },

    async deleteProfilePic() {
      try {
        this.setLoading(true);
        const result = await deleteProfilePicture(this.user.uid);

        if (result.success) {
          const updatedProfile = await getUserProfile(this.user.uid);
          this.setProfile(updatedProfile);
          this.$store.dispatch("showAlert", { 
            message: "Profile picture removed", 
            type: "success" 
          });
          this.imagePreview = null;
          this.croppedBlob = null;
        } else {
          throw new Error(result.message);
        }
      } catch (error) {
        this.$store.dispatch("showAlert", { 
          message: error.message || "Failed to delete profile picture", 
          type: "error" 
        });
      } finally {
        this.setLoading(false);
      }
    },
  },

  beforeUnmount() {
    if (this.cropper) {
      this.cropper.destroy();
    }
    if (this.imagePreview) {
      URL.revokeObjectURL(this.imagePreview);
    }
  },
};
</script>

<style scoped>
.cropper-container {
  width: 100% !important;
  height: 100% !important;
}
.cropper-modal {
  background: rgba(255, 255, 255, 0.8);
}
.cropper-line,
.cropper-point {
  background-color: rgba(30, 64, 175, 0.8); /* blue-800 */
}
input[type="file"]::file-selector-button {
  transition: all 0.2s ease;
}
input[type="file"]::file-selector-button:hover {
  background-color: #e0e7ff;
}
</style>